import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { OrangeSection } from '../../components/ui/OrangeSection'
import {
  Bot,
  Edit,
  Save,
  X,
  ArrowLeft,
  RefreshCw,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle,
  Settings,
  FileText,
  Zap
} from 'lucide-react'
import { useNotification } from '../../contexts/NotificationContext'

interface UniversalPrompt {
  id: string
  name: string
  prompt_text: string
  active: boolean
  created_at: string
  updated_at: string
}

const API_BASE = import.meta.env.VITE_API_URL || ''

const defaultPrompt = `You are an expert at analyzing resale items and creating compelling marketplace listings.

Your task is to:
1. Analyze the provided images to identify the item, its condition, brand, model, and key features
2. Create an optimized title that includes the most important keywords for search
3. Write a detailed description that highlights the item's value, condition, and appeal
4. Extract specific item details (brand, model, size, color, material, style, etc.)
5. Provide pricing guidance based on the item's condition, brand, and market value
6. Identify any potential policy flags or concerns
7. Provide a confidence score for your analysis

Focus on accuracy, marketability, and compliance with marketplace policies.`

export default function AIPromptsPage() {
  const navigate = useNavigate()
  const { success, error: showError } = useNotification()
  const [prompt, setPrompt] = useState<UniversalPrompt | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [formData, setFormData] = useState({
    name: 'Universal AI Prompt',
    prompt_text: defaultPrompt,
    active: true
  })
  const [showPreview, setShowPreview] = useState(false)

  useEffect(() => {
    fetchUniversalPrompt()
  }, [])

  const fetchUniversalPrompt = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_BASE}/api/ai-prompts-pg`)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const text = await response.text()
      if (!text) {
        throw new Error('Empty response from server')
      }

      const data = JSON.parse(text)

      if (data.success) {
        if (data.data && data.data.length > 0) {
          const firstPrompt = data.data[0]
          setPrompt(firstPrompt)
          setFormData({
            name: firstPrompt.name,
            prompt_text: firstPrompt.prompt_text,
            active: firstPrompt.active
          })
        } else {
          // No prompt exists, create a default one
          await createDefaultPrompt()
        }
      } else {
        setError('Failed to load AI prompt')
        showError('Error', data.error || 'Unknown error')
      }
    } catch (error) {
      console.error('Error fetching universal prompt:', error)
      setError('Failed to load AI prompt')
      showError('Error', error instanceof Error ? error.message : 'Failed to fetch AI prompt')
    } finally {
      setLoading(false)
    }
  }

  const createDefaultPrompt = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ai-prompts-pg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'Universal AI Prompt',
          prompt_text: defaultPrompt,
          active: true
        })
      })

      const data = await response.json()

      if (data.success) {
        setPrompt(data.data)
        setFormData({
          name: data.data.name,
          prompt_text: data.data.prompt_text,
          active: data.data.active
        })
      }
    } catch (error) {
      console.error('Error creating default prompt:', error)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)

      const response = await fetch(`${API_BASE}/api/ai-prompts-pg`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: prompt?.id,
          ...formData
        })
      })

      const data = await response.json()

      if (data.success) {
        setPrompt(data.data)
        setShowEditModal(false)
        success('Success', 'AI prompt updated successfully')
      } else {
        showError('Error', data.error)
      }
    } catch (error) {
      console.error('Error saving prompt:', error)
      showError('Error', 'Failed to save AI prompt')
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = () => {
    if (prompt) {
      setFormData({
        name: prompt.name,
        prompt_text: prompt.prompt_text,
        active: prompt.active
      })
      setShowEditModal(true)
    }
  }

  const handleToggleActive = async () => {
    if (!prompt) return

    try {
      setSaving(true)

      const response = await fetch(`${API_BASE}/api/ai-prompts-pg`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: prompt.id,
          active: !prompt.active
        })
      })

      const data = await response.json()

      if (data.success) {
        setPrompt(data.data)
        success('Success', `Prompt ${data.data.active ? 'activated' : 'deactivated'}`)
      } else {
        showError('Error', data.error)
      }
    } catch (error) {
      console.error('Error toggling prompt status:', error)
      showError('Error', 'Failed to toggle prompt status')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div style={{
        padding: '24px',
        background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
        minHeight: '100vh',
        fontFamily: 'Rajdhani, sans-serif'
      }}>
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-purple-500" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        padding: '24px',
        background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
        minHeight: '100vh',
        fontFamily: 'Rajdhani, sans-serif'
      }}>
        <div className="text-center py-12 text-white">
          <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50 text-red-400" />
          <p className="text-lg mb-2">Error Loading Prompt</p>
          <p className="text-gray-400 mb-4">{error}</p>
          <Button onClick={fetchUniversalPrompt}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF1493',
        paddingBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div>
            <h1 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '48px',
              fontWeight: 700,
              color: '#FF1493',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              margin: 0,
              textShadow: '0 0 20px rgba(255, 20, 147, 0.6)'
            }}>
              AI CORE // PROMPT ENGINE
            </h1>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '16px',
              color: '#8A95AA',
              margin: '8px 0 0 0',
              letterSpacing: '1px'
            }}>
              NEURAL NETWORK CONFIGURATION INTERFACE
            </p>
          </div>
          <Button
            onClick={() => navigate('/admin')}
            style={{
              background: 'linear-gradient(135deg, #FF1493, #C71585)',
              border: '2px solid #FF1493',
              borderRadius: '6px',
              padding: '10px 20px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 0 20px rgba(255, 20, 147, 0.4)'
            }}
          >
            <ArrowLeft style={{ width: 16, height: 16 }} />
            RETURN TO ADMIN
          </Button>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '12px',
        marginBottom: '32px'
      }}>
        {[
          {
            label: 'STATUS',
            value: prompt?.active ? 'ACTIVE' : 'INACTIVE',
            color: prompt?.active ? '#00FF00' : '#6B7280',
            icon: prompt?.active ? 'online' : 'offline'
          },
          {
            label: 'PROMPT NAME',
            value: prompt?.name || 'N/A',
            color: '#FF1493'
          },
          {
            label: 'CHARACTERS',
            value: prompt?.prompt_text.length.toLocaleString() || '0',
            color: '#00F0FF'
          },
          {
            label: 'CREATED',
            value: prompt ? new Date(prompt.created_at).toLocaleDateString() : 'N/A',
            color: '#B91BFF'
          },
          {
            label: 'UPDATED',
            value: prompt ? new Date(prompt.updated_at).toLocaleDateString() : 'N/A',
            color: '#FFD93D'
          }
        ].map((stat, idx) => (
          <div key={idx} style={{
            background: 'linear-gradient(135deg, rgba(255, 20, 147, 0.05), rgba(255, 20, 147, 0.02))',
            border: '1px solid rgba(255, 20, 147, 0.3)',
            borderRadius: '6px',
            padding: '12px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '11px',
              color: '#8A95AA',
              letterSpacing: '1px',
              marginBottom: '4px'
            }}>
              {stat.label}
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: 700,
              color: stat.color
            }}>
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      {/* Prompt Card */}
      <div style={{
        background: 'linear-gradient(135deg, #1A1F2E, #242938)',
        border: '2px solid #FF1493',
        borderRadius: '8px',
        padding: '24px',
        cursor: 'default',
        transition: 'all 0.3s ease',
        boxShadow: '0 0 20px rgba(255, 20, 147, 0.2)',
        position: 'relative',
        overflow: 'hidden',
        marginBottom: '32px'
      }}>
        {/* Animated Corner Accent */}
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '100px',
          height: '100px',
          background: 'linear-gradient(135deg, transparent 50%, rgba(255, 20, 147, 0.2) 50%)',
          pointerEvents: 'none'
        }} />

        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '16px'
        }}>
          <div style={{
            background: 'rgba(255, 20, 147, 0.2)',
            border: '2px solid #FF1493',
            borderRadius: '8px',
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Bot style={{ width: 28, height: 28, color: '#FF1493' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {prompt?.active ? (
              <CheckCircle style={{ width: 16, height: 16, color: '#00FF00' }} />
            ) : (
              <X style={{ width: 16, height: 16, color: '#FF0000' }} />
            )}
            <span style={{
              fontSize: '12px',
              color: prompt?.active ? '#00FF00' : '#FF0000',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontWeight: 700
            }}>
              {prompt?.active ? 'ONLINE' : 'OFFLINE'}
            </span>
          </div>
        </div>

        {/* Title & Description */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#FF1493',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            margin: '0 0 8px 0',
            textShadow: '0 0 10px rgba(255, 20, 147, 0.6)'
          }}>
            {prompt?.name || 'AI PROMPT'}
          </h3>
          <p style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '14px',
            color: '#8A95AA',
            margin: 0,
            letterSpacing: '0.5px'
          }}>
            Universal neural network instruction set
          </p>
        </div>

        {/* Stats */}
        <div style={{
          display: 'flex',
          gap: '20px',
          marginBottom: '16px',
          paddingTop: '16px',
          borderTop: '1px solid rgba(138, 149, 170, 0.2)'
        }}>
          <div>
            <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
              CHARACTERS
            </div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#E0E6F0' }}>
              {prompt?.prompt_text.length.toLocaleString() || 0}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
              STATUS
            </div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: prompt?.active ? '#00FF00' : '#6B7280' }}>
              {prompt?.active ? 'ACTIVE' : 'INACTIVE'}
            </div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <div style={{ fontSize: '11px', color: '#8A95AA', letterSpacing: '1px', marginBottom: '4px' }}>
              UPDATED
            </div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#E0E6F0' }}>
              {prompt ? new Date(prompt.updated_at).toLocaleDateString() : 'N/A'}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center'
        }}>
          <button
            onClick={handleEdit}
            style={{
              flex: 1,
              background: 'linear-gradient(135deg, #FFD93D, #FFA500)',
              border: '2px solid #FFD93D',
              borderRadius: '6px',
              padding: '12px 20px',
              color: '#1A1F2E',
              fontSize: '14px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              boxShadow: '0 0 20px rgba(255, 217, 61, 0.4)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 0 30px rgba(255, 217, 61, 0.6)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 217, 61, 0.4)'
            }}
          >
            <Edit style={{ width: 16, height: 16 }} />
            EDIT PROMPT
          </button>
          <button
            onClick={handleToggleActive}
            disabled={saving}
            style={{
              flex: 1,
              background: prompt?.active
                ? 'linear-gradient(135deg, #EF4444, #DC2626)'
                : 'linear-gradient(135deg, #10B981, #059669)',
              border: prompt?.active ? '2px solid #EF4444' : '2px solid #10B981',
              borderRadius: '6px',
              padding: '12px 20px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              boxShadow: prompt?.active
                ? '0 0 20px rgba(239, 68, 68, 0.4)'
                : '0 0 20px rgba(16, 185, 129, 0.4)',
              opacity: saving ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.currentTarget.style.boxShadow = prompt?.active
                  ? '0 0 30px rgba(239, 68, 68, 0.6)'
                  : '0 0 30px rgba(16, 185, 129, 0.6)'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = prompt?.active
                ? '0 0 20px rgba(239, 68, 68, 0.4)'
                : '0 0 20px rgba(16, 185, 129, 0.4)'
            }}
          >
            {saving ? (
              <>
                <RefreshCw style={{ width: 16, height: 16 }} className="animate-spin" />
                PROCESSING
              </>
            ) : prompt?.active ? (
              <>
                <X style={{ width: 16, height: 16 }} />
                DEACTIVATE
              </>
            ) : (
              <>
                <CheckCircle style={{ width: 16, height: 16 }} />
                ACTIVATE
              </>
            )}
          </button>
          <button
            onClick={() => setShowPreview(!showPreview)}
            style={{
              background: showPreview
                ? 'linear-gradient(135deg, #00F0FF, #00B8D4)'
                : 'rgba(0, 240, 255, 0.1)',
              border: '2px solid #00F0FF',
              borderRadius: '6px',
              padding: '12px 20px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              boxShadow: '0 0 20px rgba(0, 240, 255, 0.4)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 0 30px rgba(0, 240, 255, 0.6)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 240, 255, 0.4)'
            }}
          >
            {showPreview ? <EyeOff style={{ width: 16, height: 16 }} /> : <Eye style={{ width: 16, height: 16 }} />}
            {showPreview ? 'HIDE' : 'PREVIEW'}
          </button>
        </div>

        {/* Preview Section */}
        {showPreview && (
          <div style={{
            marginTop: '20px',
            padding: '20px',
            background: 'rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(0, 240, 255, 0.3)',
            borderRadius: '8px'
          }}>
            <h4 style={{
              color: '#00F0FF',
              fontWeight: 700,
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '12px'
            }}>
              PROMPT PREVIEW
            </h4>
            <div style={{
              color: '#E0E6F0',
              fontSize: '14px',
              whiteSpace: 'pre-wrap',
              maxHeight: '400px',
              overflowY: 'auto',
              fontFamily: 'monospace',
              lineHeight: '1.6'
            }}>
              {prompt?.prompt_text}
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            fontFamily: 'Rajdhani, sans-serif'
          }}
        >
          <div style={{
            background: 'linear-gradient(135deg, #1A1F2E, #242938)',
            border: '2px solid #FF1493',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '1000px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 8px 40px rgba(255, 20, 147, 0.4)'
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '2px solid rgba(255, 20, 147, 0.3)'
            }}>
              <h3 style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '24px',
                fontWeight: 700,
                color: '#FF1493',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                margin: 0,
                textShadow: '0 0 10px rgba(255, 20, 147, 0.6)'
              }}>
                EDIT AI PROMPT
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                  border: '2px solid #EF4444',
                  borderRadius: '6px',
                  padding: '8px',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 0 10px rgba(239, 68, 68, 0.4)'
                }}
              >
                <X style={{ width: 20, height: 20 }} />
              </button>
            </div>

            {/* Form Fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  color: '#8A95AA',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 700,
                  marginBottom: '8px'
                }}>
                  PROMPT NAME
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  style={{
                    background: 'rgba(0, 0, 0, 0.4)',
                    color: 'white',
                    border: '1px solid rgba(255, 20, 147, 0.3)',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '16px',
                    padding: '12px'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  color: '#8A95AA',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 700,
                  marginBottom: '8px'
                }}>
                  PROMPT TEXT
                </label>
                <textarea
                  value={formData.prompt_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, prompt_text: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid rgba(255, 20, 147, 0.3)',
                    borderRadius: '8px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    color: 'white',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                  rows={20}
                  placeholder="Enter your AI prompt here..."
                />
                <p style={{
                  fontSize: '11px',
                  color: '#8A95AA',
                  marginTop: '8px',
                  letterSpacing: '1px'
                }}>
                  {formData.prompt_text.length.toLocaleString()} CHARACTERS
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: '#00FF00'
                  }}
                />
                <label htmlFor="active" style={{
                  fontSize: '14px',
                  color: '#E0E6F0',
                  cursor: 'pointer',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  ACTIVE PROMPT
                </label>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                paddingTop: '20px',
                borderTop: '1px solid rgba(255, 20, 147, 0.2)'
              }}>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #FFD93D, #FFA500)',
                    border: '2px solid #FFD93D',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    color: '#1A1F2E',
                    fontSize: '14px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 0 20px rgba(255, 217, 61, 0.4)',
                    opacity: saving ? 0.6 : 1
                  }}
                >
                  {saving ? (
                    <>
                      <RefreshCw style={{ width: 16, height: 16 }} className="animate-spin" />
                      SAVING
                    </>
                  ) : (
                    <>
                      <Save style={{ width: 16, height: 16 }} />
                      SAVE PROMPT
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowEditModal(false)}
                  style={{
                    background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                    border: '2px solid #EF4444',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 0 20px rgba(239, 68, 68, 0.4)'
                  }}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
